import React, { useEffect, useMemo, useRef } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import './landing.css';

type PhoneDemoProps = {
  /** Nombre de messages visibles (0 à 6) */
  visible: number;
  className?: string;
  /** Affiche l'indicateur "en train d'écrire" sous le dernier message reçu */
  typing?: boolean;
};

export const PHONE_MESSAGE_COUNT = 6;

const PAY_LINK = 'https://t.me/stars_paid_bot?start=pay_xk9m2';

/** Double coche "lu" façon Telegram */
const Ticks: React.FC = () => (
  <svg className="tg-ticks" viewBox="0 0 24 14" width="16" height="10" aria-hidden="true">
    <path d="M1.5 7.5l4 4L13 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 11.5l1.5 1.5L22 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Time: React.FC<{ value: string; read?: boolean }> = ({ value, read }) => (
  <span className="tg-time">
    {value}
    {read && <Ticks />}
  </span>
);

/**
 * Maquette d'un iPhone (Telegram, thème sombre iOS) : le marchand envoie son
 * lien de paiement à un client dans une conversation privée, le client paie
 * via le bot, le marchand reçoit la confirmation.
 */
const PhoneDemo: React.FC<PhoneDemoProps> = ({ visible, className, typing = false }) => {
  const { t, locale } = useI18n();
  const listRef = useRef<HTMLDivElement>(null);

  // Date du jour façon Telegram ("12 octobre"), dans la langue courante
  const dateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(new Date());
    } catch {
      return new Date().toLocaleDateString();
    }
  }, [locale]);

  // Heure de la barre de statut
  const clock = useMemo(() => {
    const d = new Date();
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [visible, typing]);

  return (
    <div className={`ts-phone tg-phone ${className || ''}`} aria-hidden="true">
      {/* Barre de statut iOS */}
      <div className="tg-statusbar">
        <span className="tg-statusbar-time">{clock}</span>
        <span className="tg-statusbar-pill">
          <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
            <path d="M9.6 14.4l-2.2 2.2a3.2 3.2 0 0 1-4.5-4.5l3.4-3.4a3.2 3.2 0 0 1 4.5 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M14.4 9.6l2.2-2.2a3.2 3.2 0 0 1 4.5 4.5l-3.4 3.4a3.2 3.2 0 0 1-4.5 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          TELEGRAM
        </span>
        <span className="tg-statusbar-icons">
          <span className="tg-signal"><i /><i /><i /><i /></span>
          <span className="tg-battery"><i /></span>
        </span>
      </div>

      {/* En-tête : conversation privée avec un client */}
      <div className="tg-header">
        <button type="button" className="tg-back" tabIndex={-1}>
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="tg-back-badge">6</span>
        </button>
        <div className="tg-header-title">
          <div className="tg-header-name">+33 6 12 34 56 78</div>
          <div className="tg-header-sub">{t.demo.online}</div>
        </div>
        <div className="tg-header-avatar tg-header-avatar--person">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <circle cx="12" cy="8" r="4" fill="currentColor" />
            <path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" fill="currentColor" />
          </svg>
        </div>
      </div>

      {/* Fil de messages */}
      <div className="tg-chat">
        <div className="ts-phone-messages tg-messages" ref={listRef}>
          <div className="tg-date">{dateLabel}</div>

          {/* 1. Le marchand envoie son lien de paiement */}
          {visible >= 1 && (
            <div className="ts-msg ts-msg--user tg-msg tg-msg--out">
              <div className="tg-bubble">
                {t.demo.linkCreated}
                <span className="tg-link">{PAY_LINK}</span>
                <div className="tg-preview">
                  <div className="tg-preview-site">{t.demo.botName}</div>
                  <div className="tg-preview-title">
                    <span className="tg-preview-icon">★</span>
                    {t.demo.invoiceTitle}
                  </div>
                  <div className="tg-preview-desc">
                    {t.demo.invoiceDesc} · <strong>100 ⭐</strong>
                  </div>
                </div>
                <Time value="14:02" read />
              </div>
            </div>
          )}

          {/* 2. Le client répond */}
          {visible >= 2 && (
            <div className="ts-msg ts-msg--bot tg-msg tg-msg--in">
              <div className="tg-bubble">
                {t.demo.iPayNow} 👍
                <Time value="14:03" />
              </div>
            </div>
          )}

          {/* 3. Le client paie via le bot (message "via @stars_paid_bot") */}
          {visible >= 3 && (
            <div className="ts-msg ts-msg--bot tg-msg tg-msg--in tg-msg--stack">
              <div className="tg-bubble tg-sheet">
                <div className="tg-via">via <strong>@stars_paid_bot</strong></div>
                <div className="tg-sheet-label">⭐ {t.demo.paymentLabel}</div>
                <div className="tg-sheet-row">
                  <span>{t.demo.merchant}</span>
                  <strong>{t.demo.invoiceTitle}</strong>
                </div>
                <div className="tg-sheet-row">
                  <span>{t.demo.amount}</span>
                  <strong>100 ⭐</strong>
                </div>
                <Time value="14:03" />
              </div>
              <div className="tg-inline-btn tg-inline-btn--primary">{t.demo.payBtn}</div>
            </div>
          )}

          {/* 4. Confirmation du paiement reçue par le marchand */}
          {visible >= 4 && (
            <div className="ts-msg ts-msg--bot tg-msg tg-msg--in tg-msg--stack">
              <div className="tg-bubble tg-bubble--success">
                <div className="tg-via">via <strong>@stars_paid_bot</strong></div>
                <div className="tg-bubble-title">✅ {t.demo.paymentReceived}</div>
                <div className="tg-success-sub">{t.demo.invoiceCredited}</div>
                <Time value="14:03" />
              </div>
              <div className="tg-inline-btn">
                {t.demo.confirmPay}
                <svg className="tg-kb-ext" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                  <path d="M7 17L17 7M9 7h8v8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          )}

          {/* 5. Le marchand remercie */}
          {visible >= 5 && (
            <div className="ts-msg ts-msg--user tg-msg tg-msg--out">
              <div className="tg-bubble">
                {t.demo.thanks} 🎉
                <Time value="14:04" read />
              </div>
            </div>
          )}

          {/* 6. Réaction du client */}
          {visible >= 6 && (
            <div className="ts-msg ts-msg--bot tg-msg tg-msg--in">
              <div className="tg-bubble tg-bubble--emoji">
                🙏
                <Time value="14:04" />
              </div>
            </div>
          )}

          {typing && visible < PHONE_MESSAGE_COUNT && (
            <div className="ts-msg ts-msg--bot tg-msg tg-msg--in">
              <div className="ts-typing tg-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Barre de saisie */}
      <div className="ts-phone-composer tg-composer">
        <div className="tg-icon-btn">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M16.5 6.5l-7.8 7.8a2.2 2.2 0 0 0 3.1 3.1l8-8a4.2 4.2 0 0 0-6-6l-8 8a6.2 6.2 0 0 0 8.8 8.8l6.4-6.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <div className="tg-input">
          <span>{t.demo.messagePlaceholder}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <div className="tg-icon-btn">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="9" y="3" width="6" height="12" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default PhoneDemo;
